import { Component, ComponentRef, EventEmitter, Input, Output } from '@angular/core';
import { PackageModel } from '@data-layer/models/lcl';
import { TranslateService } from '@ngx-translate/core';
import { LclContainerService } from '../../services';
import { take } from 'rxjs/operators';
import { LclEditPackageComponent } from '../lcl-edit-package/lcl-edit-package.component';
import { AppService } from '@app/app.service';
import {
  ManagementConfirmedPackageModel,
  ManagementDraftPackageModel,
  ManagementPackageModel,
  ManagementUserModel,
} from '@data-layer/models/management';
import { enumActivityItem, enumPackagesConfirmationState } from '@data-layer/enums/lcl';

@Component({
  selector: 'app-lcl-packages-view',
  template: `
    <app-lcl-card cardTitle="LOAD_DETAILS" [isCanEdit]="isEditablePackages" (edit)="onEditPackages()">
      <ng-container
        header-label
        *ngIf="
          isAdminView ||
          (!isAdminView &&
            (packagesStatus === enumPackagesConfirmationState.USER_PACKAGES_CONFIRMED ||
              packagesStatus === enumPackagesConfirmationState.UPDATED_PACKAGES_CONFIRMED))
        "
      >
        <span class="packages-status" [ngClass]="packageStatusClass">
          {{ 'PACKAGES_CONFIRMATION_STATE.' + enumPackagesConfirmationState[packagesStatus] | translate }}
        </span>

        <span class="packages-status-date" *ngIf="isAdminView">
          {{ packagesConfirmationDate | customDateTimeByUtc }}
        </span>
      </ng-container>

      <ng-container header-actions-button *ngIf="isAdminView">
        <button
          type="button"
          class="button-packages-confirm"
          *ngIf="
            (packagesStatus !== enumPackagesConfirmationState.USER_PACKAGES_CONFIRMED &&
              packagesStatus !== enumPackagesConfirmationState.UPDATED_PACKAGES_CONFIRMED) ||
            isEditablePackages
          "
          (click)="confirmPackages.emit()"
        >
          {{ 'LABELS.CONFIRM_GOODS' | translate }}
        </button>
      </ng-container>

      <ng-container body>
        <div class="lcl-packages-body" [ngClass]="{ 'is-editable': isEditablePackages }">
          <div class="row">
            <div class="col-12 my-auto" [ngClass]="{ 'col-lg-8': isShipmentLayout, 'col-lg-6': !isShipmentLayout }">
              <div
                class="lcl-packages-body-notification"
                *ngIf="!isAdminView && packagesStatus === enumPackagesConfirmationState.UPDATED_PACKAGES_CONFIRMED"
              >
                <span>{{ 'LABELS.PACKAGE_GOODS_CONFIRMED_HINT' | translate }}</span>
              </div>

              <ng-container *ngIf="!isAdminView || (isAdminView && packagesStatus === enumPackagesConfirmationState.USER_DRAFT)">
                <div class="lcl-packages-item" *ngFor="let package of packages">
                  <app-lcl-packages-view-row
                    [package]="package"
                    [isCanDelete]="packages.length > 1 && isEditablePackages"
                    (removePackage)="removePackage.emit($event)"
                  ></app-lcl-packages-view-row>
                </div>
              </ng-container>

              <ng-container *ngIf="isAdminView && packagesStatus > enumPackagesConfirmationState.USER_DRAFT">
                <div class="lcl-packages-item" *ngFor="let package of joinedPackages">
                  <app-lcl-packages-view-row
                    [package]="package"
                    [isAdminView]="true"
                    [activityItem]="package.action"
                    [user]="getUser(package.modificationData.modifiedByUserId)"
                    (removePackage)="removePackage.emit($event)"
                  ></app-lcl-packages-view-row>
                </div>
              </ng-container>
            </div>

            <div class="col-12 lcl-packages-total" [ngClass]="{ 'col-lg-4': isShipmentLayout, 'col-lg-6': !isShipmentLayout }">
              <div class="row lcl-packages-total-row">
                <div class="col-12 d-none d-lg-block">
                  <h5>{{ 'LABELS.TOTALS' | translate }}</h5>
                </div>
                <div class="col-12 col-md-4 d-flex d-lg-block">
                  <span>{{ 'UNITS' | translate }}</span>
                  <h6>{{ totalPackages }}</h6>
                </div>

                <div class="col-12 col-md-4 d-flex d-lg-block pl-lg-0 pr-lg-0">
                  <span>{{ 'LABELS.WEIGHT' | translate }}</span>
                  <h6>{{ totalWeight }} {{ 'WEIGHT_UNIT.KG' | translate }}</h6>
                </div>

                <div class="col-12 col-md-4 d-flex d-lg-block pr-0">
                  <span>{{ 'LABELS.VOLUME' | translate }}</span>
                  <h6>{{ totalCBM }} {{ 'LENGTH_UNIT.CBM' | translate }}</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </app-lcl-card>
  `,
  styleUrls: ['./lcl-packages-view.component.scss'],
})
export class LclPackagesViewComponent {
  @Input() public packages: Array<PackageModel | ManagementPackageModel>;
  @Input() public draftPackages: ManagementDraftPackageModel[];
  @Input() public userDeclaredPackages: ManagementConfirmedPackageModel[];
  @Input() public users: ManagementUserModel[];
  @Input() public isEditablePackages: boolean;
  @Input() public isShipmentLayout: boolean;
  @Input() public isAdminView: boolean;
  @Input() public packagesStatus: enumPackagesConfirmationState;
  @Input() public packagesConfirmationDate: string;
  @Output() public removePackage: EventEmitter<number> = new EventEmitter<number>();
  @Output() public savePackages: EventEmitter<ManagementDraftPackageModel[]> = new EventEmitter<ManagementDraftPackageModel[]>();
  @Output() public confirmPackages: EventEmitter<void> = new EventEmitter<void>();

  public enumPackagesConfirmationState: typeof enumPackagesConfirmationState = enumPackagesConfirmationState;

  constructor(
    private translateService: TranslateService,
    private lclContainerService: LclContainerService,
    private appService: AppService,
  ) {}

  public get packagesData(): Array<PackageModel | ManagementPackageModel | ManagementDraftPackageModel> {
    return this.isAdminView && this.packagesStatus === enumPackagesConfirmationState.SUPPORT_DRAFT
      ? (this.draftPackages || []).map((item: ManagementDraftPackageModel) => ({ ...item, id: null }))
      : this.packages;
  }

  public get totalPackages(): number {
    return (this.packagesData || []).reduce((acc: number, curr: PackageModel) => acc + curr.quantity, 0);
  }

  public get totalWeight(): number {
    return this.lclContainerService.totalWeight(this.packagesData);
  }

  public get totalCBM(): number {
    return this.lclContainerService.totalCBM(this.packagesData);
  }

  public onEditPackages(): void {
    const componentSidebarRef: ComponentRef<LclEditPackageComponent> = this.appService.openSidebar(LclEditPackageComponent, {
      sidebarHeaderText: 'EDIT_LOAD_DETAILS',
    });

    componentSidebarRef.instance.packages = this.packagesData;
    componentSidebarRef.instance.isAdminView = this.isAdminView;
    componentSidebarRef.instance.isShowAdd = true;
    componentSidebarRef.instance.isUpdatedLayout = true;
    componentSidebarRef.instance.dataSaved.pipe(take(1)).subscribe((val: ManagementDraftPackageModel[]) => {
      this.savePackages.emit(val);
      this.appService.closeSidebar();
    });
  }

  public getUser(userId: string): ManagementUserModel {
    return (this.users || []).find((user: ManagementUserModel) => user.id === userId);
  }

  public get joinedPackages(): ManagementPackageModel[] {
    let result: ManagementPackageModel[] = this.packages;

    if (this.packagesStatus === enumPackagesConfirmationState.SUPPORT_DRAFT) {
      result = [];

      (this.packages || []).forEach((item: ManagementPackageModel) => {
        const draftItem: ManagementDraftPackageModel = (this.draftPackages || []).find(
          (draftItemSearch: ManagementDraftPackageModel) => draftItemSearch.originalPackageId === item.id,
        );

        if (!!draftItem?.originalPackageId) {
          // item was updated
          if (this.isSamePackage(item, draftItem)) {
            result.push({ ...item, action: enumActivityItem.NOT_MODIFIED });
          } else {
            result.push({ ...draftItem, action: enumActivityItem.UPDATED, modificationData: draftItem.modificationData });
            result.push({ ...item, action: enumActivityItem.PREVIOUS });
          }
        } else if (!draftItem) {
          // item was deleted
          result.push({ ...item, action: enumActivityItem.DELETED, modificationData: item.modificationData });
        }
      });

      // items were created
      (this.draftPackages || [])
        .filter((item: ManagementDraftPackageModel) => !item.originalPackageId)
        .forEach((item: ManagementDraftPackageModel) => {
          result.push({ ...item, action: enumActivityItem.CREATED, modificationData: item.modificationData });
        });
    }

    if (
      [enumPackagesConfirmationState.USER_PACKAGES_CONFIRMED, enumPackagesConfirmationState.UPDATED_PACKAGES_CONFIRMED].includes(
        this.packagesStatus,
      )
    ) {
      result = [];

      (this.packages || []).forEach((item: ManagementPackageModel) => {
        const userPackageItem: ManagementConfirmedPackageModel = (this.userDeclaredPackages || []).find(
          (confirmedItemSearch: ManagementConfirmedPackageModel) => confirmedItemSearch.confirmedPackageId === item.id,
        );

        if (!!userPackageItem?.confirmedPackageId) {
          // item was updated
          if (this.isSamePackage(item, userPackageItem)) {
            result.push({ ...item, action: enumActivityItem.NOT_MODIFIED });
          } else {
            result.push({ ...item, action: enumActivityItem.UPDATED, modificationData: userPackageItem.modificationData });
            result.push({ ...userPackageItem, action: enumActivityItem.PREVIOUS, modificationData: userPackageItem.modificationData });
          }
        } else if (!userPackageItem) {
          // item was added
          result.push({ ...item, action: enumActivityItem.CREATED, modificationData: item.modificationData });
        }
      });

      // items were deleted
      (this.userDeclaredPackages || [])
        .filter((item: ManagementConfirmedPackageModel) => !item.confirmedPackageId)
        .forEach((item: ManagementConfirmedPackageModel) => {
          result.push({ ...item, action: enumActivityItem.DELETED, modificationData: item.modificationData });
        });
    }

    return result;
  }

  public get packageStatusClass(): string {
    switch (this.packagesStatus) {
      case enumPackagesConfirmationState.USER_DRAFT:
        return 'booked';
      case enumPackagesConfirmationState.SUPPORT_DRAFT:
        return 'updated';
      case enumPackagesConfirmationState.USER_PACKAGES_CONFIRMED:
      case enumPackagesConfirmationState.UPDATED_PACKAGES_CONFIRMED:
        return 'confirmed';
    }
  }

  private isSamePackage(
    packageA: ManagementPackageModel,
    packageB: ManagementDraftPackageModel | ManagementConfirmedPackageModel,
  ): boolean {
    return (
      packageA?.dimensions?.length === packageB?.dimensions?.length &&
      packageA?.dimensions?.width === packageB?.dimensions?.width &&
      packageA?.dimensions?.height === packageB?.dimensions?.height &&
      packageA.weight === packageB.weight &&
      packageA.lengthUnit === packageB.lengthUnit &&
      packageA.weightUnit === packageB.weightUnit
    );
  }
}
